import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ProductItem from "../components/home/ProductItem";
import Loading from "../components/Loading";
import {
  getFilteredProducts,
  selectFilters,
} from "../redux/reducers/productSlice";

const Women = () => {
  document.title = "Digestive Wellness - HOLIVANA"
  const [sortOption, setSortOption] = useState('popularity');

  let location = useLocation();
  let gender = location.pathname.split("/")[2];
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.product.loading);
  const errMsg = useSelector((state) => state.product.errMsg);
  const error = useSelector((state) => state.product.error);
  const containFilters = useSelector((state) => state.product.containFilters);
  const filter = useSelector((state) => state.product.filter);
  const products = useSelector((state) => state.product.products);

  // Filter products for digestive collection
  const digestiveProducts = useMemo(() => {
    const keywords = ['antacid', 'gas', 'digestion', 'liver', 'piles'];
    const filtered = products.filter(product => {
      const title = product.title.toLowerCase();
      return keywords.some(keyword => title.includes(keyword));
    });

    // Apply sorting
    const sorted = [...filtered];
    switch (sortOption) {
      case 'newest':
        return sorted.reverse();
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'name-asc':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'popularity':
      default:
        return sorted;
    }
  }, [products, sortOption]);

  const filteredProducts = useSelector(
    (state) => state.product.filteredProducts
  );

  useEffect(() => {
    if (!loading) {
      dispatch(getFilteredProducts({ gender }));
      dispatch(
        selectFilters({ filter: { ...filter, color: "", company: "" } })
      );
    }
    // eslint-disable-next-line
  }, [loading, gender]);

  const displayProducts = digestiveProducts.length > 0 ? digestiveProducts : filteredProducts;

  return (
    <div className="bg-[#f6f7f4] min-h-screen">
      {/* Page Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-screen-xl mx-auto px-6 pt-8 pb-6">
          {/* Breadcrumb */}
          <nav className="text-xs text-stone-500 mb-3" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><a href="/" className="hover:text-stone-900">Home</a></li>
              <li>/</li>
              <li><a href="/collections" className="hover:text-stone-900">Collections</a></li>
              <li>/</li>
              <li aria-current="page">Digestive Wellness</li>
            </ol>
          </nav>

          {/* Title & Description */}
          <div className="space-y-3 max-w-3xl">
            <h1 className="text-3xl lg:text-4xl font-light text-stone-900 tracking-tight">
              Digestive Wellness
            </h1>
            <p className="text-stone-600 leading-relaxed text-lg">
              Support optimal gut health and digestive function with time-tested Ayurvedic remedies 
              designed to promote healthy digestion, relieve discomfort, and maintain digestive balance.
            </p>
          </div>
        </div>
      </header>

      {/* Utility Bar */}
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-stone-600">
              {displayProducts.length} {displayProducts.length === 1 ? 'product' : 'products'}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">Sort:</span>
              <select 
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="text-sm border-0 border-b border-stone-300 bg-transparent text-stone-900 focus:outline-none focus:border-emerald-700 pr-8"
              >
                <option value="popularity">Popularity</option>
                <option value="newest">Newest</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <main className="max-w-screen-xl mx-auto px-6 py-12">
        {!error ? (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loading />
              </div>
            ) : (
              <>
                {displayProducts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
                    {displayProducts.map((product, index) => (
                      <ProductItem
                        key={product._id}
                        product={product}
                        containFilter={containFilters[index]}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 text-stone-600">
                    No products found in this collection
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="text-center py-20 text-stone-600">
            {errMsg}. Please reload the page.
          </div>
        )}
      </main>

      {/* Collection Benefits */}
      <section className="bg-white border-t border-stone-200">
        <div className="max-w-screen-xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-light text-stone-900 mb-3">
              Why Choose Our Digestive Products?
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Traditional Ayurvedic solutions for modern digestive concerns
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-2xl text-emerald-700">
                🌿
              </div>
              <h3 className="text-base font-medium text-stone-900">Gut Health Support</h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Promote healthy digestion and maintain optimal gut flora balance
              </p>
            </div>
            
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-2xl text-emerald-700">
                💚
              </div>
              <h3 className="text-base font-medium text-stone-900">Natural Relief</h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Gentle, effective relief from acidity, gas, and digestive discomfort
              </p>
            </div>
            
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-2xl text-emerald-700">
                ✨
              </div>
              <h3 className="text-base font-medium text-stone-900">Liver Protection</h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Support liver function and overall digestive system health
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Women;
